// Client-side DLP scanner — mirrors the patterns enforced by migration 108
// so users see warnings before they hit Send. The server is the source of
// truth (it'll block on insert regardless), but a heads-up here saves the
// user from typing/pasting something into the wire that gets rejected.
//
// scanText(body, settings) → [{ rule, severity, match }, ...]
//   settings: same shape as firm_dlp_settings, with per-rule severity
//     'off' | 'warn' | 'block'.
//
// Patterns intentionally accept a superset of the server regexes so we
// catch borderline cases here even when the server lets them through.

const PATTERNS = [
  {
    rule:    'credit_card',
    // 13–19 digit run with possible spaces or dashes; Luhn-validated below.
    re:      /\d[\d -]{11,21}\d/g,
    needsLuhn: true,
    redact:  (m) => '****' + m.replace(/\D/g, '').slice(-4),
  },
  {
    rule:    'ssn_us',
    re:      /\b\d{3}-\d{2}-\d{4}\b/g,
    redact:  (m) => '***-**-' + m.slice(-4),
  },
  {
    rule:    'ghana_card',
    re:      /\bGHA-\d{9}-\d\b/g,
    redact:  (m) => 'GHA-*****' + m.slice(-4),
  },
  {
    rule:    'api_key',
    re:      /sk_(?:test|live)_[A-Za-z0-9]{20,}|AKIA[0-9A-Z]{16}/g,
    redact:  (m) => m.slice(0, 6) + '…' + m.slice(-4),
  },
  {
    rule:    'jwt',
    re:      /eyJ[A-Za-z0-9_-]+\.eyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+/g,
    redact:  (m) => m.slice(0, 10) + '…' + m.slice(-6),
  },
  {
    rule:    'iban',
    re:      /\b[A-Z]{2}\d{2}[A-Z0-9]{11,30}\b/g,
    redact:  (m) => m.slice(0, 4) + '…' + m.slice(-4),
  },
];

function luhnValid(digitsOnly) {
  if (!/^\d{13,19}$/.test(digitsOnly)) return false;
  let sum = 0;
  let alt = false;
  for (let i = digitsOnly.length - 1; i >= 0; i--) {
    let d = digitsOnly.charCodeAt(i) - 48;
    if (alt) {
      d *= 2;
      if (d > 9) d -= 9;
    }
    sum += d;
    alt = !alt;
  }
  return sum % 10 === 0;
}

// Maps the rule key to the corresponding setting field.
function severityFor(settings, rule) {
  if (!settings || settings.enabled === false) return 'off';
  return settings[`rule_${rule}`] || 'off';
}

export function scanText(body, settings) {
  if (!body || typeof body !== 'string' || body.length === 0) return [];
  if (!settings || settings.enabled === false) return [];
  const hits = [];
  for (const p of PATTERNS) {
    const severity = severityFor(settings, p.rule);
    if (severity === 'off') continue;
    // Reset regex state — these are /g regexes so we re-create lastIndex.
    p.re.lastIndex = 0;
    let m;
    while ((m = p.re.exec(body))) {
      const raw = m[0];
      if (p.needsLuhn) {
        const digits = raw.replace(/\D/g, '');
        if (!luhnValid(digits)) continue;
      }
      hits.push({ rule: p.rule, severity, match: p.redact(raw) });
    }
  }
  return hits;
}

// Human-friendly rule labels for the warning banner.
export const DLP_RULE_LABELS = {
  credit_card: 'Credit card number',
  ssn_us:      'US Social Security number',
  ghana_card:  'Ghana Card number',
  api_key:     'API key',
  jwt:         'JWT / auth token',
  iban:        'IBAN bank account',
};
